import type { ReactNode } from 'react'
import { Megaphone, RotateCw, TriangleAlert } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { cn } from '@/shared/lib/utils'
import { CHAT_COPY, useBroadcasts, type Broadcast } from '@/entities/chat'
import { formatRelativeDateTime } from '@/shared/lib/DateUtil'

function byNewest(a: Broadcast, b: Broadcast): number {
  return new Date(b.sentOn).getTime() - new Date(a.sentOn).getTime()
}

export function BroadcastList({
  selectedId,
  onSelect,
  composeAction,
}: {
  selectedId: number | null
  onSelect: (id: number) => void
  composeAction?: ReactNode
}) {
  const { data, isLoading, error, refetch } = useBroadcasts(null)

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 px-6 py-8 text-center">
        <TriangleAlert className="h-6 w-6 text-bad" strokeWidth={1.6} />
        <p className="text-[13px] font-medium text-ink">{CHAT_COPY.listFailed}</p>
        <Button variant="outline" size="sm" className="mt-1" onClick={() => void refetch()}>
          <RotateCw className="h-3.5 w-3.5" />
          {CHAT_COPY.retry}
        </Button>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 p-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="flex flex-col gap-2">
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-3 w-3/4" />
          </div>
        ))}
      </div>
    )
  }

  const broadcasts = [...(data ?? [])].sort(byNewest)

  if (broadcasts.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-10 text-center text-[13px] text-ink-3">
        <Megaphone className="h-6 w-6 text-ink-4" strokeWidth={1.6} />
        <p>{CHAT_COPY.noBroadcasts}</p>
        {composeAction}
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {broadcasts.map((broadcast) => {
        const readRatio =
          broadcast.recipientCount > 0 ? Math.round((broadcast.readCount / broadcast.recipientCount) * 100) : 0
        return (
          <button
            key={broadcast.id}
            type="button"
            onClick={() => onSelect(broadcast.id)}
            className={cn(
              'flex w-full flex-col items-start gap-1 border-b border-line-2 px-3 py-2.5 text-left transition-colors hover:bg-hover',
              broadcast.id === selectedId && 'bg-brand-accent-soft',
            )}
          >
            <p className="truncate text-[11.5px] font-medium text-ink-3">{`To ${broadcast.audienceName}`}</p>
            <p className="line-clamp-2 wrap-anywhere text-[12.5px] text-ink">{broadcast.body}</p>
            <div className="mt-0.5 flex w-full items-center gap-2">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-hover">
                <div className="h-full rounded-full bg-brand-accent" style={{ width: `${readRatio}%` }} />
              </div>
              <span className="shrink-0 text-[11px] text-ink-3">{`${broadcast.readCount}/${broadcast.recipientCount}`}</span>
            </div>
            <p className="text-[11px] text-ink-4">{formatRelativeDateTime(broadcast.sentOn)}</p>
          </button>
        )
      })}
    </div>
  )
}
